import { FileText, FileImage, Download, Trash2, Lock } from "lucide-react";
import api from "@/lib/api";

const FMT_ICONS = {
  pdf: FileText, doc: FileText, docx: FileText,
  jpg: FileImage, jpeg: FileImage, png: FileImage,
};

function prettyBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export async function downloadDoc(doc) {
  try {
    const { data } = await api.get(`/documents/${doc.id}/download`);
    // Open in new tab — Cloudinary signed URL with attachment flag triggers download
    window.open(data.url, "_blank", "noopener,noreferrer");
  } catch (err) {
    alert("Could not generate download link: " + (err.response?.data?.detail || err.message));
  }
}

/**
 * Read-only display of one document row with download + (optional) delete.
 * Caller controls whether delete is allowed via `canDelete`.
 */
export default function DocumentRow({ doc, canDelete, onDeleted }) {
  const ext = (doc.file_format || "").toLowerCase();
  const Icon = FMT_ICONS[ext] || FileText;

  const remove = async () => {
    if (!window.confirm(`Permanently delete "${doc.name}"?`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      onDeleted?.(doc.id);
    } catch (err) {
      alert("Could not delete: " + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="flex items-center gap-4 p-4 rounded-lg" style={{ background: 'rgba(11,19,43,0.5)', border: '1px solid var(--border-color)' }} data-testid={`doc-row-${doc.id}`}>
      <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0" style={{ background: 'rgba(212,163,115,0.12)', color: 'var(--accent)' }}>
        <Icon size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-medium truncate" data-testid={`doc-name-${doc.id}`}>{doc.name}</div>
        <div className="text-xs flex items-center gap-3 mt-0.5" style={{ color: 'var(--text-muted)' }}>
          <span className="uppercase">{ext}</span>
          <span>·</span>
          <span>{prettyBytes(doc.bytes)}</span>
          <span>·</span>
          <span>{new Date(doc.created_at).toLocaleDateString()}</span>
          {doc.uploaded_by_role === "admin" && (
            <>
              <span>·</span>
              <span style={{ color: 'var(--accent)' }}>From Coastal Bend</span>
            </>
          )}
        </div>
      </div>
      <button
        onClick={() => downloadDoc(doc)}
        className="btn-ghost !py-1.5 !px-3 !text-xs"
        title="Download (signed link, expires in 5 min)"
        data-testid={`doc-download-${doc.id}`}
      >
        <Download size={13} /> Download
      </button>
      {canDelete && (
        <button
          onClick={remove}
          className="text-red-400 hover:text-red-300 p-2"
          title="Delete document"
          data-testid={`doc-delete-${doc.id}`}
        >
          <Trash2 size={15} />
        </button>
      )}
    </div>
  );
}

export function PrivacyBadge() {
  return (
    <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-md" style={{ background: 'rgba(34,197,94,0.08)', color: '#86efac', border: '1px solid rgba(34,197,94,0.18)' }}>
      <Lock size={13} />
      Private vault — only you and Coastal Bend admins can see these files.
    </div>
  );
}
